import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Stack,
  CircularProgress,
} from '@mui/material';
import { Warning as WarningIcon } from '@mui/icons-material';

interface SessionTimeoutWarningDialogProps {
  open: boolean;
  secondsRemaining: number;
  onExtendSession: () => void;
  onLogout: () => void;
}

/**
 * Warning dialog displayed before auto-logout due to inactivity.
 * Shows a countdown timer and allows the user to extend their session or logout immediately.
 */
export const SessionTimeoutWarningDialog: React.FC<SessionTimeoutWarningDialogProps> = ({
  open,
  secondsRemaining,
  onExtendSession,
  onLogout,
}) => {
  // Format seconds as M:SS
  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Calculate progress (for CircularProgress visual)
  // Assuming 2 minutes (120 seconds) as max warning time
  const maxWarningSeconds = 120;
  const progress = (secondsRemaining / maxWarningSeconds) * 100;

  return (
    <Dialog
      open={open}
      maxWidth="xs"
      fullWidth
      // Prevent closing by clicking outside or pressing Escape
      onClose={() => {}}
      disableEscapeKeyDown
      PaperProps={{
        sx: {
          borderTop: 4,
          borderColor: 'warning.main',
        },
      }}
    >
      <DialogTitle>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <WarningIcon color="warning" sx={{ fontSize: 28 }} />
          <Typography variant="h6" component="span" fontWeight={600}>
            Session Expiring
          </Typography>
        </Stack>
      </DialogTitle>

      <DialogContent>
        <Stack spacing={3} alignItems="center" sx={{ py: 2 }}>
          <Typography variant="body1" color="text.secondary" textAlign="center">
            Your session will expire due to inactivity.
          </Typography>

          {/* Countdown Timer Display */}
          <Box sx={{ position: 'relative', display: 'inline-flex' }}>
            <CircularProgress
              variant="determinate"
              value={progress}
              size={100}
              thickness={4}
              sx={{
                color: secondsRemaining <= 30 ? 'error.main' : 'warning.main',
              }}
            />
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                bottom: 0,
                right: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Typography
                variant="h4"
                component="span"
                fontWeight={700}
                color={secondsRemaining <= 30 ? 'error.main' : 'text.primary'}
              >
                {formatTime(secondsRemaining)}
              </Typography>
            </Box>
          </Box>

          <Typography variant="body2" color="text.secondary" textAlign="center">
            Click "Extend Session" to continue working.
          </Typography>
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3, gap: 1 }}>
        <Button
          onClick={onLogout}
          variant="outlined"
          color="inherit"
          sx={{ flex: 1 }}
        >
          Logout Now
        </Button>
        <Button
          onClick={onExtendSession}
          variant="contained"
          color="primary"
          sx={{ flex: 1 }}
          autoFocus
        >
          Extend Session
        </Button>
      </DialogActions>
    </Dialog>
  );
};
